import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import { MessageService } from 'primeng/api';

import { ClientTemplateService } from '../../../services/client-template.service';
import { DynamicTemplateConfigService } from '../../../services/pdf-generation/dynamic-template-config.service';
import { PdfGenerationService } from '../../../services/pdf-generation/pdf-generation.service';
import {
  compressLogoForPdf,
  compressQrCodeForPdf,
  compressSignatureForPdf,
  logImageSize,
} from '../../../services/pdf-generation/pdf-image-utils';
import { PdfUploadService } from '../../../services/pdf-upload.service';
import { PosCalculationService } from './pos-calculation.service';

export interface DashboardPdfContext {
  invoice: any;
  storename: any;
  selectedCustomer: any;
  clientId: string | null;
  isDecimalQuantityEnabled: boolean;
  isUnitPriceInclTax?: boolean;
  generatePdfFallback?: () => void;
}

export interface DashboardCreditPdfContext {
  selectedInvoiceData: any;
  products: any[];
  formData: any;
  userName?:string;
  isInterStateTax: () => boolean;
  getEffectivePrice: (product: any) => number;
  currentOrderId: string;
  selectedInvoiceNo: string;
  customer?:any;
}

/**
 * DashboardPdfService
 *
 * Dedicated service for generating Invoice PDFs (generatePdfNew & fallback generatePdf) and
 * Exchange Credit Note PDFs (generateCreditPdf).
 *
 * Extracted from DashboardComponent to improve component performance and maintainability
 * while preserving 100% of the existing PDF generation & upload business logic.
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardPdfService {
  private lastInvoicePdf: { invoiceNo: string; invoiceData: any; templateName: string } | null = null;

  constructor(
    private clientTemplateService: ClientTemplateService,
    private dynamicTemplateConfigService: DynamicTemplateConfigService,
    private pdfGenerationService: PdfGenerationService,
    private pdfUploadService: PdfUploadService,
    private messageService: MessageService,
    private posCalculationService: PosCalculationService,
  ) { }

  /**
   * Convert image URL to base64 data URL
   */
  async convertImageUrlToBase64(url: string): Promise<string | null> {
    if (!url) return null;

    if (url.startsWith('data:')) {
      return url;
    }

    try {
      const response = await fetch(url);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('[DashboardPdfService] Error converting image URL to base64:', error);
      return null;
    }
  }

  /**
   * Fallback Thermal Invoice PDF Generator
   */
  /**
   * Unit price to print for a saved invoice line. Incl-tax priced lines save a taxable
   * total that is off price x qty by rounding (3 x 268.29 = 804.87, saved 804.86), so the
   * line is priced from the saved total (268.2867, still prints 268.29) and every
   * template's price x qty and price + tax / qty add up to the bill (901.44).
   * Older lines (total = price x qty) and before-tax discounted lines keep their price.
   */
  private getPdfLinePrice(item: any, qty: number): number {
    const price = Number(item.price) || 0;
    const savedTotal = Number(item.total);
    const hasBeforeTaxDiscount = !item.discaftertax && Number(item.discountamt) > 0;
    // Rounding gap allowed: excl price (0.005 x qty) + line value and GST (up to 0.015)
    if (
      hasBeforeTaxDiscount ||
      !(qty > 0) ||
      !Number.isFinite(savedTotal) ||
      Math.abs(savedTotal - price * qty) > 0.01 * qty + 0.02
    ) {
      return price;
    }
    return savedTotal / qty;
  }

  async generatePdf(ctx: DashboardPdfContext): Promise<void> {
    const invoiceData = ctx.invoice.invoice;
    const clientData = this.dynamicTemplateConfigService.getClientData();
    const storeStateName = clientData?.state_name || '';
    const customerState = ctx.selectedCustomer?.state || '';

    const isSameState =
      storeStateName.trim().toLowerCase() ===
      customerState.trim().toLowerCase();
    const isInterState = !isSameState;

    console.log(
      `[Thermal PDF] Store: "${storeStateName}", Customer: "${customerState}" => ${isInterState ? 'IGST' : 'CGST+SGST'}`,
    );

    const itemLevelDiscount = invoiceData.discountamt || 0;
    const overallDiscount = invoiceData.overalldiscount || 0;
    const totalDiscount = itemLevelDiscount + overallDiscount;
    const showInclusive = invoiceData.discaftertax;
    const tableItemsTotal = invoiceData.salesitems.reduce((sum: number, item: any) => {
      const qty = item.quantity || 1;
      const taxPerUnit = (item.taxamount || 0) / qty;
      const inclusivePrice = this.getPdfLinePrice(item, qty) + taxPerUnit;
      return sum + (showInclusive ? inclusivePrice * qty : item.total || 0);
    }, 0);
    const posPaperWidthPoints = 3.14 * 72;
    const addLine = {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: posPaperWidthPoints - 20,
          y2: 0,
          lineWidth: 0.5,
        },
      ],
      margin: [0, 5, 0, 5],
    };
    const filteredSalesItems = invoiceData.salesitems;
    const tableData = [
      [
        { text: 'Name', style: 'tableHeader' },
        { text: 'MRP', style: 'tableHeader' },
        { text: 'Qty', style: 'tableHeader' },
        { text: 'Price', style: 'tableHeader' },
        { text: 'Total', style: 'tableHeader' },
      ],
      ...filteredSalesItems.map((item: any) => {
        const qty = item.quantity || 1;
        const taxPerUnit = (item.taxamount || item.taxAmount || 0) / qty;
        const linePrice = this.getPdfLinePrice(item, qty);

        const displayPrice = showInclusive
          ? (linePrice + taxPerUnit).toFixed(2)
          : item.price.toFixed(2);

        const displayTotal = showInclusive
          ? ((linePrice + taxPerUnit) * qty).toFixed(2)
          : item.grandtotal;
 const isFreeItem = Number(item.is_free) === 1;
        return [
          (item.name || 'N/A') + (isFreeItem ? ' [Free]' : '') + `\nHSN:${item.hsncode || 'NA'}`,
          item.mrp.toFixed(2),
          item.quantity,
          displayPrice,
          displayTotal,
        ];
      }),
    ];

    const paymenttypeData = [
      [
        { text: 'Payment Type', style: 'tableHeader' },
        { text: 'Amount', style: 'tableHeader' },
      ],
    ];

    if (invoiceData.cashamount > 0) {
      paymenttypeData.push([
        { text: 'CASH', style: 'tableBody' },
        { text: tableItemsTotal.toFixed(2) + '', style: 'tableBody' },
      ]);
    }
    if (invoiceData.cardamount > 0) {
      paymenttypeData.push([
        { text: 'CARD', style: 'tableBody' },
        { text: invoiceData.cardamount + '', style: 'tableBody' },
      ]);
    }
    if (invoiceData.upiamount > 0) {
      paymenttypeData.push([
        { text: 'UPI', style: 'tableBody' },
        { text: invoiceData.upiamount + '', style: 'tableBody' },
      ]);
    }
    if (invoiceData.redeemamount > 0) {
      paymenttypeData.push([
        { text: 'REDEEM', style: 'tableBody' },
        { text: invoiceData.redeemamount + '', style: 'tableBody' },
      ]);
    }

    const taxdata = [];

    if (isInterState) {
      taxdata.push([
        { text: 'Taxable Value', style: 'tableHeader' },
        { text: 'IGST', style: 'tableHeader' },
      ]);

      const totalTax =
        (invoiceData.ctaxval || 0) +
        (invoiceData.staxval || 0) +
        (invoiceData.itaxval || 0);
      taxdata.push([
        { text: (invoiceData.total || 0) + '', style: 'tableBody' },
        { text: totalTax + '', style: 'tableBody' },
      ]);
    } else {
      taxdata.push([
        { text: 'Taxable Value', style: 'tableHeader' },
        { text: 'CGST', style: 'tableHeader' },
        { text: 'SGST', style: 'tableHeader' },
      ]);

      taxdata.push([
        { text: (invoiceData.total || 0) + '', style: 'tableBody' },
        { text: (invoiceData.ctaxval || 0) + '', style: 'tableBody' },
        { text: (invoiceData.staxval || 0) + '', style: 'tableBody' },
      ]);
    }

    let storeaddress = '';
    if (ctx.storename) {
      storeaddress += ctx.storename.name + ',\n';
      storeaddress += ctx.storename.address + ',\n';
      storeaddress += ctx.storename.address1 + ',\n';
      storeaddress += ctx.storename.address2 + ',\n';
      storeaddress += ctx.storename.pincode + ',\n';
      storeaddress += ctx.storename.mobile + ',\n';
    }

    let pageSize: { width: number; height: string } = {
      width: posPaperWidthPoints,
      height: 'auto',
    };
    let pageMargins = [13, 10, 10, 10];
    if (parseInt(ctx.clientId ?? '') == 134) {
      pageSize = { width: 595.28, height: 'auto' };
      pageMargins = [40, 60, 40, 60];
    }
    let docDefinition: any = {
      pageSize: pageSize,
      pageMargins: pageMargins,
      content: [
        { text: 'Tax Invoice', style: 'header', alignment: 'center' },
        { ...addLine },
        { text: storeaddress, style: 'bold', alignment: 'center', fontSize: 9 },
        { ...addLine },
        {
          columns: [
            {
              text: `Bill No: ${invoiceData.invoiceno}`,
              italics: true,
              width: '*',
            },
            {
              text: `Date: ${invoiceData.invoicedate?.slice(0, 10) || 'N/A'}`,
              alignment: 'right',
              width: '*',
            },
          ],
          margin: [0, 5, 0, 5],
        },
        {
          columns: [
            {
              text: `Customer: ${invoiceData.name}`,
              italics: true,
              width: '*',
            },
            {
              text: `Mobile: ${invoiceData.user_id}`,
              alignment: 'right',
              width: '*',
            },
          ],
          margin: [0, 5, 0, 5],
        },
        { ...addLine },
        {
          style: 'tableExample',
          table: {
            widths: [40, 30, 20, 40, 40],
            body: tableData.map((row: any[]) =>
              row.map((cell: any) => ({ text: cell, fontSize: 8 })),
            ),
          },
          layout: {
            hLineWidth: function (i: any, node: any) {
              return i === 0 || i === node.table.body.length ? 0.5 : 0.2;
            },
            vLineWidth: function (i: any, node: any) {
              return 0.2;
            },
            hLineColor: function (i: any, node: any) {
              return 'gray';
            },
            vLineColor: function (i: any, node: any) {
              return 'gray';
            },
          },
        },
        { ...addLine },
        {
          style: 'tableExample',
          table: {
            widths: [150, 'auto'],
            body: paymenttypeData.map((row: any[]) =>
              row.map((cell: any) => ({ text: cell, fontSize: 8 })),
            ),
          },
          layout: {
            hLineWidth: function (i: any, node: any) {
              return i === 0 || i === node.table.body.length ? 0.1 : 0.1;
            },
            vLineWidth: function (i: any, node: any) {
              return 0.1;
            },
            hLineColor: function (i: any, node: any) {
              return 'gray';
            },
            vLineColor: function (i: any, node: any) {
              return 'gray';
            },
          },
        },
        { ...addLine },
        {
          style: 'tableExample',
          table: {
            widths: isInterState ? [100, 'auto'] : [100, 'auto', 'auto'],
            body: taxdata.map((row: any[]) =>
              row.map((cell: any) => ({ text: cell, fontSize: 8 })),
            ),
          },
          layout: {
            hLineWidth: function (i: any, node: any) {
              return i === 0 || i === node.table.body.length ? 0.1 : 0.1;
            },
            vLineWidth: function (i: any, node: any) {
              return 0.1;
            },
            hLineColor: function (i: any, node: any) {
              return 'gray';
            },
            vLineColor: function (i: any, node: any) {
              return 'gray';
            },
          },
        },
        { ...addLine },
        {
          width: '*',
          alignment: 'right',
          fontSize: 8,
          text: [
            itemLevelDiscount > 0
              ? `\nItem Discount: ₹${itemLevelDiscount?.toFixed(2) || '0.00'}/-`
              : '',
            overallDiscount > 0
              ? `\nOverall Discount: ₹${overallDiscount?.toFixed(2) || '0.00'}/-`
              : '',
            totalDiscount > 0
              ? `\nTotal Discount: ₹${totalDiscount?.toFixed(2) || '0.00'}/-`
              : '',
            {
              text: `\n\nFinal Amount: ₹${invoiceData.grandtotal?.toFixed(2) || '0.00'}/-`,
              style: 'bold',
              fontSize: 10,
            },
          ].filter(Boolean),
          margin: [0, 5, 10, 0],
        },
        {
          width: '*',
          alignment: 'center',
          fontSize: 6,
          text: [
            {
              text: `\n *** THANK YOU VISIT AGAIN ***`,
              style: 'bold',
              fontSize: 12,
            },
            {
              text: `\n \n This is computer generated invoice does not require signature`,
              fontSize: 6,
            },
          ],
          margin: [0, 5, 10, 0],
        },
      ],
      styles: {
        header: {
          fontSize: 12,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        tableHeader: {
          bold: true,
          fontSize: 8,
        },
        tableExample: {
          margin: [0, 5, 0, 15],
        },
        rightme: {
          alignment: 'right',
        },
      },
      defaultStyle: {
        fontSize: 7,
      },
    };

    const pdfDocGenerator = pdfMake.createPdf({
      ...(docDefinition as any),
      compress: false,
    });

    try {
      pdfDocGenerator.open();
    } catch (error) {
      console.warn('PDF popup blocked, downloading instead:', error);
      pdfDocGenerator.download(
        `invoice-${ctx.invoice.invoice.invoiceno || 'receipt'}.pdf`,
      );
    }
  }

  /**
   * Generate Invoice PDF using dynamic templates
   */
  /** PDF of the last invoice generated on this till, if it matches invoiceNo */
  async getLastInvoicePdfBlob(invoiceNo: string): Promise<Blob | null> {
    if (!invoiceNo || this.lastInvoicePdf?.invoiceNo !== invoiceNo) return null;
    return this.pdfGenerationService.generatePdfBlob(
      this.lastInvoicePdf.invoiceData,
      this.lastInvoicePdf.templateName,
    );
  }

  async generatePdfNew(
    ctx: DashboardPdfContext,
    isViewingExistingInvoice: boolean = false,
  ): Promise<void> {
    const defaultTemplate = this.clientTemplateService.getDefaultTemplate();

    if (!defaultTemplate || defaultTemplate.template_id === 1) {
      console.log(
        'No default template configured or template ID is 1, using fallback thermal template (old method)',
      );
      if (ctx.generatePdfFallback) {
        ctx.generatePdfFallback();
      } else {
        this.generatePdf(ctx);
      }
      return;
    }

    const templateName = this.clientTemplateService.getTemplateNameById(
      defaultTemplate.template_id,
    );
    console.log(templateName);

    // Convert logo URL to base64
    const logoUrl = this.dynamicTemplateConfigService.getLogo();
    const logoRaw = await this.convertImageUrlToBase64(logoUrl).catch(
      (e: any) => {
        console.error('[PDF] Logo conversion failed, skipping:', e);
        return null;
      },
    );
    const logoBase64 = logoRaw ? await compressLogoForPdf(logoRaw) : null;
    logImageSize('logo (after compression)', logoBase64 ?? '');

    // Get signature and qrcode from API client data and convert to base64
    const clientDatas = this.dynamicTemplateConfigService.getClientData();
    console.log('[PDF] clientData.signature:', clientDatas?.signature);
    console.log('[PDF] clientData.qrcode:', clientDatas?.qrcode);

    const signatureRaw = clientDatas?.signature
      ? await this.convertImageUrlToBase64(clientDatas.signature).catch(
        (e: any) => {
          console.error('[PDF] Signature conversion failed, skipping:', e);
          return null;
        },
      )
      : null;
    const signatureBase64 = signatureRaw
      ? await compressSignatureForPdf(signatureRaw)
      : null;
    logImageSize('signature (after compression)', signatureBase64 ?? '');

    const qrcodeRaw = clientDatas?.qrcode
      ? await this.convertImageUrlToBase64(clientDatas.qrcode).catch(
        (e: any) => {
          console.error('[PDF] QR code conversion failed, skipping:', e);
          return null;
        },
      )
      : null;
    const qrcodeBase64 = qrcodeRaw
      ? await compressQrCodeForPdf(qrcodeRaw)
      : null;
    logImageSize('qrcode (after compression)', qrcodeBase64 ?? '');

    const invoiceData = ctx.invoice.invoice;
    const salesItems = invoiceData.salesitems;

    console.log('--- INVOICE INFO ---');
    console.log('Invoice No:', invoiceData.invoiceno);
    console.log('Raw Date/Time from API:', invoiceData.invoicedate);
    console.log(
      'customer_rewardpoints from invoice.invoice:',
      invoiceData.customer_rewardpoints,
    );
    console.log(
      'customer_rewardpoints from invoice:',
      (ctx.invoice as any).customer_rewardpoints,
    );

    // Calculate item-level discount only (not including overall discount)
    const itemLevelDiscount = salesItems.reduce((sum: number, item: any) => {
      const itemDiscount = (item.discountamt || 0) * (item.quantity || 1);
      return sum + itemDiscount;
    }, 0);

    // Calculate overall discount separately
    const overallDiscount = salesItems.reduce((sum: number, item: any) => {
      const globalDiscount = item.globaldiscountshare || 0;
      return sum + globalDiscount;
    }, 0);

    const totalDiscount = itemLevelDiscount + overallDiscount;
    console.log(ctx.storename);
    const addressParts = [
      ctx.storename?.address,
      ctx.storename?.address1,
      ctx.storename?.city,
      ctx.storename?.address2,
      ctx.storename?.pincode,
    ].filter((part) => part && part.trim());
    const businessAddress = addressParts.join(', ');

    const rawDate = invoiceData.invoicedate;
    const invoiceDate = new Date(rawDate);
    const rawDateTime = invoiceData.createdAt;
    const invoiceDateTimeForTime = new Date(rawDateTime);

    const formattedDate = invoiceDate
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '-');

    const clientData = this.dynamicTemplateConfigService.getClientData();
    console.log('[PDF] Client data from API:', clientData);

    const msmeNo =
      clientData?.msmeno ||
      clientData?.msme_no ||
      clientData?.msmeNo ||
      ctx.storename?.msmeno ||
      ctx.storename?.msme_no ||
      ctx.storename?.msmeNo ||
      localStorage.getItem('msmeno') ||
      '';

    const msmeType =
      clientData?.msmetype ||
      clientData?.msme_type ||
      clientData?.msmeType ||
      ctx.storename?.msmetype ||
      ctx.storename?.msme_type ||
      ctx.storename?.msmeType ||
      localStorage.getItem('msmetype') ||
      '';

    const apiBusinessData = clientData
      ? {
        gstno: clientData.gstno || '',
        email: clientData.email || '',
        mobile: clientData.mobile || '',
        url: clientData.url || '',
        pan_no: clientData.pan_no || '',
        address: clientData.address || '',
        address1: clientData.address1 || '',
        address2: clientData.address2 || '',
        client_name: clientData.client_name || '',
        dl_no:clientData.dl_no || '',
        fassi_no:clientData.fassi_no || '',
        cin_no:clientData.cin_no || '',
        msmeno: msmeNo,
        msmetype: msmeType,
        msmeNo: msmeNo,
        msmeType: msmeType,
      }
      : null;
    console.log(
      '[PDF] Extracted business data from API client data:',
      apiBusinessData?.pan_no,
      'MSME:', msmeNo, msmeType
    );

    const invoiceDataForPdf = {
      invoiceNumber: invoiceData.invoiceno || '',
      invoiceDate: formattedDate,
      invoiceTime: invoiceDateTimeForTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),

      business: {
        name: apiBusinessData?.client_name || ctx.storename?.name || '',
        logo: logoBase64 || '',
        tagline: '',
        address: businessAddress,
        phone: apiBusinessData?.mobile || ctx.storename?.mobile || '',
        email: apiBusinessData?.email || ctx.storename?.email || '',
        gstin:
          apiBusinessData?.gstno ||
          ctx.storename?.gstno ||
          ctx.storename?.gstin ||
          '',
        website: apiBusinessData?.url || ctx.storename?.website || '',
        pan: apiBusinessData?.pan_no,
        tan: ctx.storename?.tan || '',
        dl_no:apiBusinessData?.dl_no || '',
        fassi_no:apiBusinessData?.fassi_no || '',
        cin_no:apiBusinessData?.cin_no || '',
        msmeNo: msmeNo,
        msmeType: msmeType,
        msmeno: msmeNo,
        msmetype: msmeType,
      },

      businessName: apiBusinessData?.client_name || ctx.storename?.name || '',
      businessAddress: businessAddress,
      businessPhone: ctx.storename?.mobile || '',
      businessGSTIN:
        apiBusinessData?.gstno ||
        ctx.storename?.gstno ||
        ctx.storename?.gstin ||
        '',
      businessEmail: apiBusinessData?.email || ctx.storename?.email || '',

      party: ctx.selectedCustomer
        ? {
          name: ctx.selectedCustomer.customer_name || '',
          address:
            [
              ctx.selectedCustomer.address_line1,
              ctx.selectedCustomer.address_line2,
              ctx.selectedCustomer.address_line3,
            ]
              .filter((line) => line && line.trim())
              .join(', ') || '',
          city: ctx.selectedCustomer.city || '',
          state: ctx.selectedCustomer.state || '',
          stateCode: '',
          pincode: ctx.selectedCustomer.zip_code || '',
          phone: ctx.selectedCustomer.mobile_no || '',
          mobile: ctx.selectedCustomer.mobile_no || '',
          email: ctx.selectedCustomer.email || '',
          gstin: ctx.selectedCustomer.gst_no || '',
          balance: 0,
        }
        : {
          name: invoiceData.name || '',
          address: '',
          city: '',
          state: '',
          stateCode: '',
          pincode: '',
          phone: invoiceData.user_id ? String(invoiceData.user_id) : '',
          mobile: invoiceData.user_id ? String(invoiceData.user_id) : '',
          email: '',
          gstin: '',
          balance: 0,
        },

      customerName:
        ctx.selectedCustomer?.customer_name || invoiceData.name || '',
      customerMobile:
        ctx.selectedCustomer?.mobile_no ||
        (invoiceData.user_id ? String(invoiceData.user_id) : ''),
      customerGSTIN: ctx.selectedCustomer?.gst_no || '',
      customerAddress: ctx.selectedCustomer
        ? {
          line1: ctx.selectedCustomer.address_line1 || '',
          line2: ctx.selectedCustomer.address_line2 || '',
          line3: ctx.selectedCustomer.address_line3 || '',
          city: ctx.selectedCustomer.city || '',
          state: ctx.selectedCustomer.state || '',
          country: ctx.selectedCustomer.country || '',
          zipCode: ctx.selectedCustomer.zip_code || '',
          pincode: ctx.selectedCustomer.zip_code || '',
        }
        : {
          line1: '',
          line2: '',
          line3: '',
          city: '',
          state: '',
          country: '',
          zipCode: '',
          pincode: '',
        },

      shipping: invoiceData.shippingaddress
        ? (() => {
          const addressParts = invoiceData.shippingaddress.split(';');
          return {
            name: invoiceData.name || '',
            address: addressParts[0] || '',
            city: addressParts[2] || '',
            state: addressParts[3] || '',
            pincode: addressParts[4] || '',
            phone: invoiceData.user_id ? String(invoiceData.user_id) : '',
            email: ctx.selectedCustomer?.email || '',
            gstin: ctx.selectedCustomer?.gst_no || '',
          };
        })()
        : undefined,

      dispatchFrom: {
        name: ctx.storename?.name || '',
        address:
          [
            ctx.storename?.address,
            ctx.storename?.address1,
            ctx.storename?.address2,
          ]
            .filter((part) => part && part.trim())
            .join(', ') || '',
        city: ctx.storename?.city || '',
        state: ctx.storename?.state || '',
        pincode: ctx.storename?.pincode || '',
        phone: ctx.storename?.mobile || apiBusinessData?.mobile || '',
        email: apiBusinessData?.email || ctx.storename?.email || '',
        gstin: apiBusinessData?.gstno || ctx.storename?.gstno || '',
      },

      vehicleNumber: invoiceData.vehicleno || '',
      lrNo: invoiceData.lrno || '',
      transporterName: invoiceData.transportername || '',
      deliveryChallan: invoiceData.deliverychallan || '',
      dispatchedThrough: invoiceData.transportername || '',
      shippingAddress: invoiceData.shippingaddress || '',
      destination: invoiceData.shippingaddress
        ? (() => {
          const addressParts = invoiceData.shippingaddress.split(';');
          return addressParts[2] || '';
        })()
        : '',

      referenceNo: '',
      referenceDate: '',
      sodcNo: invoiceData.deliverychallan || '',
      sodcDate: invoiceData.invoicedate
        ? new Date(invoiceData.invoicedate)
          .toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
          .replace(/\//g, '-')
        : new Date()
          .toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
          .replace(/\//g, '-'),
      ewayBillNumber: '',
      ewayBillDate: '',

      items: salesItems.map((item: any, index: number) => {
        const itemLevelDiscount = item.discountamt || 0;

        let quantity = parseFloat(item.quantity) || 0;
        if (!ctx.isDecimalQuantityEnabled) {
          item.quantity = Math.round(quantity);
        } else {
          item.quantity = parseFloat(quantity.toFixed(2));
        }
        item.originalprice = item.originalprice;
        // Local copy only: invoice.salesitems is read again by return/exchange flows
        const price = this.getPdfLinePrice(item, item.quantity);
        const taxableAmount = price * item.quantity - itemLevelDiscount;
        const cgstAmount =
          item.ctaxval || taxableAmount * ((item.ctax || 0) / 100);
        const sgstAmount =
          item.staxval || taxableAmount * ((item.stax || 0) / 100);
        const igstAmount =
          item.itaxval || taxableAmount * ((item.itax || 0) / 100);
        const discountPercent =
          item.quantity > 0 && price > 0
            ? (
              (itemLevelDiscount / (price * item.quantity)) *
              100
            ).toFixed(2)
            : '0';

        return {
          sno: index + 1,
          name: item.name,
          hsn: item.hsncode || item.hsn,
          hsncode: item.hsncode || item.hsn,
          batch: item.batch || item.batchid || item.batch_no || '',
          expDate: item.expDate || item.expiry_date || item.expdate || item.expirydate || item.exp_date || item.expiryDate || '',
          expdate: item.expdate || item.expDate || item.expiry_date || item.expirydate || item.exp_date || item.expiryDate || '',
          expiry_date: item.expiry_date || item.expDate || item.expdate || item.expirydate || item.exp_date || item.expiryDate || '',
          quantity: item.quantity,
          unit: item.unit || 'PCS',
          price: price,
          originalprice: item.originalprice,
          amount: price * item.quantity,
          discount: itemLevelDiscount,
          discountAmount: itemLevelDiscount,
          discountamt: itemLevelDiscount,
          discountPercent: item.discount,
          discountPercentage: parseFloat(discountPercent),
          taxableAmount: taxableAmount,
          taxRate: item.taxpercentage,
          taxPercent: item.taxpercentage,
          taxAmount: item.taxamount || 0,
          taxamount: item.taxamount || 0,
          total: item.total,
          netValue: item.grandtotal,
          sku: item.sku,
          skuid: item.sku,
          tax: item.taxpercentage,
          cgst: item.ctax,
          sgst: item.stax,
          igst: item.itax || 0,
          cgstval: cgstAmount,
          sgstval: sgstAmount,
          igstval: igstAmount,
          cess: item.cess || 0,
          discaftertax: item.discaftertax ? 1 : 0,
          mrp: item.mrp,
          description: item.description,
          product: item.product,
          is_free: Number(item.is_free) || 0,
          selected_mapping: item.selected_mapping || null,
          warranty_date: item.warranty_date || null,
        };
      }),

      subtotal: invoiceData.subtotal,
      discaftertax: invoiceData.discaftertax ? 1 : 0,
      discount: totalDiscount,
      itemDiscount: itemLevelDiscount,
      overallDiscount: overallDiscount,
      taxAmount: invoiceData.taxamount,
      totalTax: invoiceData.taxamount,
      cgst: invoiceData.ctaxval || 0,
      sgst: invoiceData.staxval || 0,
      igst: invoiceData.itaxval || 0,
      roundOff: invoiceData.roundoffamount
        ? parseFloat(invoiceData.roundoffamount)
        : 0,
      roundOffAmount: invoiceData.roundoffamount
        ? parseFloat(invoiceData.roundoffamount)
        : 0,
      grandTotal: invoiceData.grandtotal,
      totalAmount: invoiceData.grandtotal,
      paidamount: invoiceData.paidamount || 0,
      balanceamount: invoiceData.balanceamount || 0,
      cashAmount: invoiceData.cashamount || 0,
      cardAmount: invoiceData.cardamount || 0,
      upiAmount: invoiceData.upiamount || 0,

      loyaltyAmount: Number(invoiceData.redeempointamt) || 0,
      loyaltyPoints:
        Number(invoiceData.customer_rewardpoints) ||
        Number((ctx.invoice as any).customer_rewardpoints) ||
        0,
      redeemedPoints:
        Number(invoiceData.rewardpoints) ||
        Number((ctx.invoice as any).rewardpoints) ||
        0,
      consumedPoints:
        Number(invoiceData.redeempoints) ||
        Number((ctx.invoice as any).redeempoints) ||
        0,

      bankDetails: this.dynamicTemplateConfigService.getBankAccount()
        ? {
          bankName:
            this.dynamicTemplateConfigService.getBankAccount().bankname || '',
          accountNumber:
            this.dynamicTemplateConfigService.getBankAccount()
              .accountnumber || '',
          ifscCode:
            this.dynamicTemplateConfigService.getBankAccount().ifsc || '',
          accountHolderName:
            this.dynamicTemplateConfigService.getBankAccount().accountname ||
            '',
          upiId:
            this.dynamicTemplateConfigService.getBankAccount().upiid || '',
        }
        : undefined,

      qrCodeData:
        this.dynamicTemplateConfigService.getBankAccount()?.upiid || '',

      invoiceType: 'sale' as const,
      isUnitPriceInclTax: ctx.isUnitPriceInclTax === true,

      hsnBreakup: this.calculateHSNBreakup(salesItems),

      paymentMethod: invoiceData.modeofpayment || '',
      signature: signatureBase64 || '',
      qrcode: qrcodeBase64 || '',
      terms: clientData.terms || ''
    };

    if (this.dynamicTemplateConfigService.isConfigCached()) {
      console.log('[Dashboard] Using cached client data');
      this.applyTaxTypeAndGeneratePdf(
        ctx,
        invoiceDataForPdf,
        templateName,
        isViewingExistingInvoice,
      );
    } else {
      const clientId = parseInt(ctx.clientId ?? '');
      if (!clientId) {
        this.messageService.add({
          severity: 'error',
          summary: 'PDF Generation Error',
          detail: 'Client ID not found. Please refresh the page.',
          life: 3000,
        });
        return;
      }

      this.dynamicTemplateConfigService
        .preloadTemplateConfig(clientId)
        .subscribe({
          next: () => {
            console.log('[Dashboard] Client data preloaded successfully');
            this.applyTaxTypeAndGeneratePdf(
              ctx,
              invoiceDataForPdf,
              templateName,
              isViewingExistingInvoice,
            );
          },
          error: (error) => {
            console.error('[Dashboard] Error preloading client data:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'PDF Generation Error',
              detail: 'Failed to load client data. Please try again.',
              life: 3000,
            });
          },
        });
    }
  }

  /**
   * Determine tax type (IGST vs CGST_SGST)
   */
  private determineTaxType(
    storeStateName: string,
    invoiceData: any,
    selectedCustomerState: string,
  ): 'IGST' | 'CGST_SGST' {
    if (!storeStateName) {
      console.warn(
        '[Tax Type] Store state not available, defaulting to CGST+SGST',
      );
      return 'CGST_SGST';
    }

    const shippingState = invoiceData.shipping?.state || '';
    const partyState = invoiceData.party?.state || '';
    const customerAddressState = invoiceData.customerAddress?.state || '';

    const customerState =
      shippingState ||
      partyState ||
      customerAddressState ||
      selectedCustomerState;

    if (!customerState) {
      return 'CGST_SGST';
    }

    const isSameState =
      storeStateName.trim().toLowerCase() ===
      customerState.trim().toLowerCase();

    const taxType = isSameState ? 'CGST_SGST' : 'IGST';

    console.log(
      `[Tax Type] Store: "${storeStateName}", Customer: "${customerState}" => ${taxType}`,
    );

    return taxType;
  }

  /**
   * Apply Tax Type & generate / upload invoice PDF
   */
  private async applyTaxTypeAndGeneratePdf(
    ctx: DashboardPdfContext,
    invoiceData: any,
    templateName: string,
    isViewingExistingInvoice: boolean = false,
  ): Promise<void> {
    const clientData = this.dynamicTemplateConfigService.getClientData();
    const storeStateName = clientData?.state_name || '';

    const selectedCustomerState = ctx.selectedCustomer?.state || '';
    const taxType = this.determineTaxType(
      storeStateName,
      invoiceData,
      selectedCustomerState,
    );

    if (taxType === 'IGST') {
      console.log('[Tax Type] Applying IGST (inter-state transaction)');

      if (invoiceData.items && Array.isArray(invoiceData.items)) {
        invoiceData.items = invoiceData.items.map((item: any) => {
          const totalTax =
            (item.cgstAmount || item.cgstval || 0) +
            (item.sgstAmount || item.sgstval || 0) +
            (item.igstAmount || item.igstval || 0);
          return {
            ...item,
            igstAmount: totalTax,
            igstval: totalTax,
            igstPercentage: item.taxPercentage || item.itax || 0,
            igst: item.taxPercentage || item.itax || 0,
            cgstAmount: 0,
            cgstval: 0,
            cgstPercentage: 0,
            cgst: 0,
            sgstAmount: 0,
            sgstval: 0,
            sgstPercentage: 0,
            sgst: 0,
          };
        });
      }

      const totalTax =
        (invoiceData.cgst || 0) +
        (invoiceData.sgst || 0) +
        (invoiceData.igst || 0);
      invoiceData.igst = totalTax;
      invoiceData.cgst = 0;
      invoiceData.sgst = 0;

      if (invoiceData.hsnBreakup && Array.isArray(invoiceData.hsnBreakup)) {
        invoiceData.hsnBreakup = invoiceData.hsnBreakup.map((hsn: any) => {
          const totalTax =
            (hsn.cgstAmount || 0) +
            (hsn.sgstAmount || 0) +
            (hsn.igstAmount || 0);
          const igstPercent = hsn.igstPercent || 0;
          return {
            ...hsn,
            igstAmount: totalTax,
            igstPercent: igstPercent,
            cgstAmount: 0,
            cgstPercent: 0,
            sgstAmount: 0,
            sgstPercent: 0,
          };
        });
      }
    } else {
      console.log('[Tax Type] Applying CGST+SGST (intra-state transaction)');

      if (invoiceData.items && Array.isArray(invoiceData.items)) {
        invoiceData.items = invoiceData.items.map((item: any) => {
          const totalTax =
            (item.cgstAmount || item.cgstval || 0) +
            (item.sgstAmount || item.sgstval || 0) +
            (item.igstAmount || item.igstval || 0);
          const halfTax = totalTax / 2;
          const halfPercentage =
            (item.taxPercentage || item.ctax || item.stax || 0) / 2;
          return {
            ...item,
            cgstAmount: halfTax,
            cgstval: halfTax,
            cgstPercentage: halfPercentage,
            cgst: halfPercentage,
            ctax: halfPercentage,
            sgstAmount: halfTax,
            sgstval: halfTax,
            sgstPercentage: halfPercentage,
            sgst: halfPercentage,
            stax: halfPercentage,
            igstAmount: 0,
            igstval: 0,
            igstPercentage: 0,
            igst: 0,
            itax: 0,
          };
        });
      }

      const totalTax =
        (invoiceData.cgst || 0) +
        (invoiceData.sgst || 0) +
        (invoiceData.igst || 0);
      const halfTax = totalTax / 2;
      invoiceData.cgst = halfTax;
      invoiceData.sgst = halfTax;
      invoiceData.igst = 0;

      if (invoiceData.hsnBreakup && Array.isArray(invoiceData.hsnBreakup)) {
        invoiceData.hsnBreakup = invoiceData.hsnBreakup.map((hsn: any) => {
          const totalTax =
            (hsn.cgstAmount || 0) +
            (hsn.sgstAmount || 0) +
            (hsn.igstAmount || 0);
          const halfTax = totalTax / 2;
          const totalPercent =
            (hsn.cgstPercent || 0) +
            (hsn.sgstPercent || 0) +
            (hsn.igstPercent || 0);
          const halfPercent = totalPercent / 2;
          return {
            ...hsn,
            cgstAmount: halfTax,
            cgstPercent: halfPercent,
            sgstAmount: halfTax,
            sgstPercent: halfPercent,
            igstAmount: 0,
            igstPercent: 0,
          };
        });
      }
    }

    this.pdfGenerationService.openInvoicePdf(invoiceData, templateName);
    // Kept so WhatsApp share right after the sale doesn't wait for the Azure upload
    this.lastInvoicePdf = { invoiceNo: invoiceData.invoiceNumber, invoiceData, templateName };

    // Skip upload only if the invoice already has a PDF in Azure or explicitly skipped
    const existingPdf = (ctx.invoice?.invoice as any)?.invoicepdf;
    if (isViewingExistingInvoice && existingPdf) {
      console.log(
        '[PDF Upload] Invoice already has PDF in Azure, skipping upload:',
        existingPdf,
      );
      return;
    }

    const invoiceUuid = ctx.invoice?.invoice?.uuid;
    if (invoiceUuid) {
      try {
        console.log(
          '[PDF Upload] PDF not in Azure. Generating blob and uploading for invoice:',
          invoiceUuid,
        );
        const blob = await this.pdfGenerationService.generatePdfBlob(
          invoiceData,
          templateName,
        );

        const invoiceNumber = ctx.invoice?.invoice?.invoiceno || 'unknown';
        const fileName = `invoice_${invoiceNumber}_${Date.now()}.pdf`;

        this.pdfUploadService
          .uploadInvoicePdf(blob, invoiceUuid, fileName)
          .subscribe({
            next: (response) => {
              console.log('[PDF Upload] Upload response from Azure:', response);
              this.messageService.add({
                severity: 'success',
                summary: 'PDF Saved',
                detail: 'Invoice PDF uploaded to Azure',
                life: 2000,
              });
            },
            error: (error) => {
              console.error(
                '[PDF Upload] Failed to upload invoice PDF to Azure:',
                error,
              );
              this.messageService.add({
                severity: 'warn',
                summary: 'Upload Failed',
                detail: 'Failed to upload PDF to Azure',
                life: 3000,
              });
            },
          });
      } catch (error) {
        console.error('[PDF Upload] Error generating PDF blob:', error);
        this.messageService.add({
          severity: 'warn',
          summary: 'Upload Failed',
          detail: 'Failed to generate PDF for upload',
          life: 3000,
        });
      }
    } else {
      console.warn('[PDF Upload] No invoice UUID found, skipping upload');
    }
  }

  /**
   * Compute HSN summary table breakup
   */
  calculateHSNBreakup(salesItems: any[]): any[] {
    const hsnMap = new Map<string, any>();

    salesItems.forEach((item: any) => {
      const hsnCode = item.hsncode || 'N/A';
      const taxableAmount =
        this.getPdfLinePrice(item, item.quantity) * item.quantity -
        (item.discountamt || 0);
      const cgstAmount =
        item.ctaxval || taxableAmount * ((item.ctax || 0) / 100);
      const sgstAmount =
        item.staxval || taxableAmount * ((item.stax || 0) / 100);
      const igstAmount =
        item.itaxval || taxableAmount * ((item.itax || 0) / 100);
      const totalTax = cgstAmount + sgstAmount + igstAmount;

      if (hsnMap.has(hsnCode)) {
        const existing = hsnMap.get(hsnCode);
        existing.taxableValue += taxableAmount;
        existing.cgstAmount += cgstAmount;
        existing.sgstAmount += sgstAmount;
        existing.igstAmount += igstAmount;
        existing.totalTax += totalTax;
      } else {
        hsnMap.set(hsnCode, {
          hsnCode: hsnCode,
          taxableValue: taxableAmount,
          cgstPercent: item.ctax || 0,
          cgstAmount: cgstAmount,
          sgstPercent: item.stax || 0,
          sgstAmount: sgstAmount,
          igstPercent: item.itax || 0,
          igstAmount: igstAmount,
          totalTax: totalTax,
        });
      }
    });

    return Array.from(hsnMap.values());
  }

  /**
   * Generate Exchange Credit Note PDF & upload
   */
  async generateCreditPdf(
    ctx: DashboardCreditPdfContext,
    exchangedItems?: any[],
  ): Promise<void> {
    const invoiceData = ctx.selectedInvoiceData.invoice;
    const itemsToCredit =
      exchangedItems ||
      ctx.products.filter((product) => {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        return exchangeQty > 0;
      });

    if (itemsToCredit.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Items to Exchange',
        detail:
          'Please select items with exchange quantities before generating credit note',
        life: 4000,
      });
      return;
    }

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const formattedTime = currentDate.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const posPaperWidthPoints = 3.14 * 72;
    const addLine = {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: posPaperWidthPoints - 20,
          y2: 0,
          lineWidth: 0.5,
        },
      ],
      margin: [0, 5, 0, 5],
    };

    // Same per-line maths as the exchange total on screen (PosCalculationService.getExactTotal):
    // price + tax + cess, less the after-tax line discount, less the line's prorated share
    // of the original invoice's overall discount.
    const isInterState = ctx.isInterStateTax();
    const creditLines = itemsToCredit.map((product: any) => {
      const exchangeQty = Number(product.exchangeQuantity) || 0;
      const effectivePrice = Number(ctx.getEffectivePrice(product));
      const itemSubtotal = effectivePrice * exchangeQty;

      const taxRate = isInterState
        ? Number(product.itax || 0)
        : Number(product.ctax || 0) + Number(product.stax || 0);
      const taxAmount = ((effectivePrice * taxRate) / 100) * exchangeQty;
      const cessAmount =
        ((effectivePrice * Number(product.cess || 0)) / 100) * exchangeQty;

      const lineDiscount =
        product.discaftertax == 1
          ? (Number(product.discountval) || 0) * exchangeQty
          : 0;
      const overallDiscount =
        this.posCalculationService.getProratedGlobalDiscountShare(
          product,
          exchangeQty,
        );

      const grossTotal = itemSubtotal + taxAmount + cessAmount - lineDiscount;
      return {
        product,
        exchangeQty,
        grossTotal,
        overallDiscount,
        netTotal: Math.max(0, grossTotal - overallDiscount),
      };
    });

    const totalOverallDiscount = creditLines.reduce(
      (sum, line) => sum + line.overallDiscount,
      0,
    );
    const totalCreditAmount = Math.max(
      0,
      creditLines.reduce((sum, line) => sum + line.grossTotal, 0) -
      totalOverallDiscount,
    );

    const creditItemsData = [
      [
        { text: 'Item Name', style: 'tableHeader' },
        { text: 'HSN', style: 'tableHeader' },
        { text: 'Qty', style: 'tableHeader' },
        { text: 'MRP', style: 'tableHeader' },
        { text: 'Total', style: 'tableHeader' },
      ],
      ...creditLines.map((line) => [
        line.product.name || 'N/A',
        line.product.hsncode || 'N/A',
        line.exchangeQty,
        `₹${line.product.mrp?.toFixed(2) || '0.00'}`,
        `₹${line.netTotal.toFixed(2)}`,
      ]),
    ];
    console.log(invoiceData,'sales return')

    let docDefinition: any = {
      pageSize: { width: posPaperWidthPoints, height: 'auto' },
      pageMargins: [13, 10, 10, 10],
      content: [
        {
          text: 'CREDIT NOTE',
          style: 'header',
          alignment: 'center',
          color: '#dc2626',
        },
        { ...addLine },
        {
          columns: [
            {
              text: `Date: ${formattedDate}`,
              width: '*',
              fontSize: 8,
              style: 'bold',
            },
            {
              text: `Time: ${formattedTime}`,
              alignment: 'right',
              width: '*',
              fontSize: 8,
              style: 'bold',
            },
          ],
          margin: [0, 5, 0, 5],
        },
        {
          columns: [
            {
              text: `Invoice No: ${invoiceData.invoiceno}`,
              width: '*',
              fontSize: 8,
              style: 'bold',
            },
            {
              text: `Cashier Name: ${ctx.formData?.salesRep || ctx.userName || invoiceData.salesrep || 'N/A'}`,
              alignment: 'right',
              width: '*',
              fontSize: 8,
              style: 'bold',
            },
          ],
          margin: [0, 5, 0, 5],
        },
        {
          columns: [
            {
              text: `Customer: ${ctx.customer.name || 'N/A'}`,
              width: '*',
              fontSize: 8,
            },
            {
              text: `Mobile: ${ctx.customer.mobileNo || 'N/A'}`,
              alignment: 'right',
              width: '*',
              fontSize: 8,
            },
          ],
          margin: [0, 5, 0, 5],
        },
        {
          columns: [
            {
              text: `Original Invoice Date: ${new Date(invoiceData.invoicedate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`,
              width: '*',
              fontSize: 8,
            },
            {
              text: `Payment Mode: ${invoiceData.modeofpayment || 'N/A'}`,
              alignment: 'right',
              width: '*',
              fontSize: 8,
            },
          ],
          margin: [0, 5, 0, 5],
        },
        { ...addLine },
        {
          text: 'ITEMS CREDITED:',
          style: 'bold',
          fontSize: 9,
          margin: [0, 5, 0, 5],
        },
        {
          style: 'tableExample',
          table: {
            widths: [55, 30, 18, 30, 40],
            body: creditItemsData.map((row) =>
              row.map((cell) => ({ text: cell, fontSize: 7 })),
            ),
          },
          layout: {
            hLineWidth: function (i: any, node: any) {
              return i === 0 || i === node.table.body.length ? 0.5 : 0.2;
            },
            vLineWidth: function (i: any, node: any) {
              return 0.2;
            },
            hLineColor: function (i: any, node: any) {
              return 'gray';
            },
            vLineColor: function (i: any, node: any) {
              return 'gray';
            },
          },
        },
        { ...addLine },
        ...(totalOverallDiscount > 0
          ? [
            {
              alignment: 'right',
              fontSize: 8,
              text: `Overall Discount: -₹${totalOverallDiscount.toFixed(2)}/-`,
              margin: [0, 2, 10, 2],
            },
          ]
          : []),
        {
          width: '*',
          alignment: 'center',
          fontSize: 12,
          text: [
            {
              text: `Credit Amount: ₹${totalCreditAmount?.toFixed(2) || '0.00'}/-`,
              style: 'bold',
              fontSize: 14,
              color: '#dc2626',
            },
            {
              text: `\nRedeem Code: ${ctx.currentOrderId || '---'}`,
              style: 'bold',
              fontSize: 12,
              color: '#269f34',
            },
          ],
          margin: [0, 10, 0, 10],
          background: '#fef2f2',
          border: [true, true, true, true],
          borderColor: '#dc2626',
        },
        {
          width: '*',
          alignment: 'center',
          fontSize: 8,
          text: [
            { text: `\nReason: Exchange Transaction`, style: 'bold' },
            {
              text: `\nCredit Note Generated: ${formattedDate} at ${formattedTime}`,
              fontSize: 7,
            },
            {
              text: `\n\n*** CREDIT NOTE FOR EXCHANGE ***`,
              style: 'bold',
              fontSize: 10,
              color: '#dc2626',
            },
            { text: `\nThis is a computer generated credit note`, fontSize: 6 },
          ],
          margin: [0, 10, 0, 10],
        },
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        tableHeader: {
          bold: true,
          fontSize: 8,
        },
        tableExample: {
          margin: [0, 5, 0, 15],
        },
      },
      defaultStyle: {
        fontSize: 7,
      },
    };

    const pdfDocGenerator = pdfMake.createPdf({
      ...(docDefinition as any),
      compress: false,
    });
    // open() runs after an async HTTP response (no user gesture), so the
    // browser may block the popup — fall back to a download like the invoice PDF.
    try {
      pdfDocGenerator.open();
    } catch (error) {
      console.warn('Credit note popup blocked, downloading instead:', error);
      pdfDocGenerator.download(
        `credit_note_${ctx.selectedInvoiceNo || 'receipt'}.pdf`,
      );
    }

    const invoiceUuid = ctx.currentOrderId;
    if (invoiceUuid) {
      try {
        const blob = await new Promise<Blob>((resolve) => {
          pdfDocGenerator.getBlob((blob) => resolve(blob));
        });

        const fileName = `credit_note_${ctx.selectedInvoiceNo}_${Date.now()}.pdf`;

        this.pdfUploadService
          .uploadCreditNotePdf(blob, invoiceUuid, fileName)
          .subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'PDF Saved',
                detail: 'Credit note PDF uploaded',
                life: 2000,
              });
            },
            error: (error) => {
              this.messageService.add({
                severity: 'warn',
                summary: 'Upload Failed',
                detail: 'Failed to upload PDF',
                life: 3000,
              });
            },
          });
      } catch (error) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Upload Failed',
          detail: 'Failed to generate PDF for upload',
          life: 3000,
        });
      }
    } else {
      console.warn(
        '[PDF Upload] No invoice UUID found, skipping credit note upload',
      );
    }
  }
}
